import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import AdSenseScript from '../../components/AdSenseScript';
import Breadcrumb from '../../components/ui/Breadcrumb';
import AdSenseUnit from '../../components/AdSenseUnit';
import CalculatorForm from './components/CalculatorForm';
import ResultsDisplay from './components/ResultsDisplay';
import AdPlacement from './components/AdPlacement';
import FAQSection from './components/FAQSection';
import DisclaimerSection from './components/DisclaimerSection';
import Footer from '../localized-landing-pages/components/Footer';
import { detectLanguage } from '../../utils/detectLanguage';

const MoneyDurationCalculator = () => {
  const location = useLocation();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [results, setResults] = useState(null);

  useEffect(() => {
    // Detect language from URL path using safe utility
    const detectedLang = detectLanguage(location?.pathname);
    
    setCurrentLanguage(detectedLang);
    localStorage.setItem('selectedLanguage', detectedLang);
    localStorage.setItem('preferredLanguage', detectedLang);
  }, [location]);

  useEffect(() => {
    const handleLanguageChange = () => {
      const newLanguage = localStorage.getItem('selectedLanguage');
      if (newLanguage) {
        setCurrentLanguage(newLanguage);
      }
    };

    window.addEventListener('storage', handleLanguageChange);
    return () => window.removeEventListener('storage', handleLanguageChange);
  }, []);

  const metaContent = {
    en: {
      title: "How Long Will My Money Last Calculator | Financial Planning Tool",
      description: "Calculate exactly how long your savings will last based on monthly expenses. Get instant results with SAFE, WARNING, or CRITICAL status indicators for better financial planning."
    },
    es: {
      title: "Calculadora de Duración del Dinero | Herramienta de Planificación Financiera",
      description: "Calcule exactamente cuánto durarán sus ahorros según los gastos mensuales. Obtenga resultados instantáneos con indicadores de estado SEGURO, ADVERTENCIA o CRÍTICO para una mejor planificación financiera."
    },
    pt: {
      title: "Calculadora de Duração do Dinheiro | Ferramenta de Planejamento Financeiro",
      description: "Calcule exatamente quanto tempo sua poupança durará com base nas despesas mensais. Obtenha resultados instantâneos com indicadores de status SEGURO, AVISO ou CRÍTICO para melhor planejamento financeiro."
    },
    fr: {
      title: "Calculateur de Durée d'Argent | Outil de Planification Financière",
      description: "Calculez exactement combien de temps votre épargne durera en fonction des dépenses mensuelles. Obtenez des résultats instantanés avec des indicateurs d'état SÛR, ATTENTION ou CRITIQUE pour une meilleure planification financière."
    },
    de: {
      title: "Geldlaufzeit-Rechner | Finanzplanungstool",
      description: "Berechnen Sie genau, wie lange Ihre Ersparnisse basierend auf monatlichen Ausgaben reichen werden. Erhalten Sie sofortige Ergebnisse mit SICHER, WARNUNG oder KRITISCH Statusindikatoren für bessere Finanzplanung."
    }
  };

  const pageContent = {
    en: {
      heading: "How Long Will My Money Last?",
      subheading: "Calculate exactly when your savings will run out based on your current monthly expenses. Get instant insights with our financial planning calculator."
    },
    es: {
      heading: "¿Cuánto Durará Mi Dinero?",
      subheading: "Calcule exactamente cuándo se agotarán sus ahorros según sus gastos mensuales actuales. Obtenga información instantánea con nuestra calculadora de planificación financiera."
    },
    pt: {
      heading: "Quanto Tempo Meu Dinheiro Vai Durar?",
      subheading: "Calcule exatamente quando sua poupança acabará com base em suas despesas mensais atuais. Obtenha insights instantâneos com nossa calculadora de planejamento financeiro."
    },
    fr: {
      heading: "Combien de Temps Mon Argent Durera-t-il?",
      subheading: "Calculez exactement quand votre épargne s'épuisera en fonction de vos dépenses mensuelles actuelles. Obtenez des informations instantanées avec notre calculateur de planification financière."
    },
    de: {
      heading: "Wie Lange Wird Mein Geld Reichen?",
      subheading: "Berechnen Sie genau, wann Ihre Ersparnisse aufgebraucht sein werden, basierend auf Ihren aktuellen monatlichen Ausgaben. Erhalten Sie sofortige Einblicke mit unserem Finanzplanungsrechner."
    }
  };

  const meta = metaContent?.[currentLanguage] || metaContent?.en;
  const content = pageContent?.[currentLanguage] || pageContent?.en;

  const calculateDuration = ({ savings, expenses }) => {
    const months = savings / expenses;
    const days = Math.floor(months * 30.44);
    
    const currentDate = new Date();
    const runOutDate = new Date(currentDate);
    runOutDate?.setDate(runOutDate?.getDate() + days);

    const formatDate = (date) => {
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      if (currentLanguage === 'en') {
        return date?.toLocaleDateString('en-US', options);
      } else if (currentLanguage === 'es') {
        return date?.toLocaleDateString('es-ES', options);
      } else if (currentLanguage === 'pt') {
        return date?.toLocaleDateString('pt-BR', options);
      } else if (currentLanguage === 'fr') {
        return date?.toLocaleDateString('fr-FR', options);
      } else if (currentLanguage === 'de') {
        return date?.toLocaleDateString('de-DE', options);
      }
      return date?.toLocaleDateString('en-US', options);
    };

    setResults({
      runOutDate: formatDate(runOutDate),
      days: days,
      months: months?.toFixed(1)
    });
  };

  return (
    <>
      <Helmet>
        <title>{metaContent?.[currentLanguage]?.title}</title>
        <meta name="description" content={metaContent?.[currentLanguage]?.description} />
        <meta name="google-adsense-account" content="ca-pub-2004114604148310" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <AdSenseScript />
        
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
            <Breadcrumb />

            <AdSenseUnit />

            <div className="text-center mb-8 md:mb-10 lg:mb-12">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground mb-4">
                {content?.heading}
              </h1>
              <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
                {content?.subheading}
              </p>
            </div>

            <CalculatorForm 
              onCalculate={calculateDuration} 
              currentLanguage={currentLanguage}
            />

            {results && (
              <>
                <ResultsDisplay 
                  results={results} 
                  currentLanguage={currentLanguage}
                />
                <AdPlacement position={1} />
              </>
            )}

            <AdSenseUnit />
            
            <FAQSection currentLanguage={currentLanguage} />

            <DisclaimerSection currentLanguage={currentLanguage} />
          </div>
        </main>

        <Footer language={currentLanguage} />
      </div>
    </>
  );
};

export default MoneyDurationCalculator;